import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  HStack,
  Link,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FaExternalLinkAlt, FaUserPlus } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { addProfileToPilot } from "../../api/searchApi";
import { batchColorScheme, scoreColorScheme, TIER_COLOR } from "./colors";

const ProfileCard = ({ profile, searchId, buId, onUpdated }) => {
  const { t } = useTranslation("portal");
  const toast = useToast();
  const [isAdding, setIsAdding] = useState(false);

  // Which load surfaced this profile (1 = initial search, 2 = first "Load 12
  // more", ...). The coloured left border isolates each batch visually; the
  // tooltip names it so the colour is identifiable without the legend.
  const batchIndex = profile.batchIndex || 1;
  const batchScheme = batchColorScheme(batchIndex);

  const matched = profile.matchedSkills || {};
  const tierChips = ["required", "optional"].flatMap((tier) =>
    (matched[tier] || []).map((label) => ({
      tier,
      label,
      color: TIER_COLOR[tier],
    }))
  );

  const locationLabel = [profile.locationCity, profile.locationCountry]
    .filter(Boolean)
    .join(", ");

  const handleAdd = async () => {
    setIsAdding(true);
    try {
      const result = await addProfileToPilot(searchId, profile.id, { buId });
      toast({
        title: t("asapProfile.profileCard.addedToast"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onUpdated?.({
        ...profile,
        matchedConsultantId: result.matchedConsultantId || result.consultantId,
      });
    } catch (err) {
      console.error("Add to PILOT failed", err);
      const detail = err?.response?.data?.detail;
      const lookup = {
        PROFILE_ALREADY_MATCHED: t("asapProfile.profileCard.errors.alreadyMatched"),
      };
      toast({
        title: lookup[detail] || t("asapProfile.profileCard.errors.addFailed"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Box
      borderWidth="1px"
      borderLeftWidth="5px"
      borderLeftColor={`${batchScheme}.400`}
      borderRadius="lg"
      bg="white"
      p={4}
      title={t("asapProfile.batch.item", { number: batchIndex })}
      data-testid={`profile-card-${profile.id}`}
      data-batch-index={batchIndex}
    >
      <HStack justify="space-between" align="start" mb={2}>
        <Box flex="1">
          <HStack mb={1} spacing={2} align="center">
            <Text
              fontWeight="semibold"
              fontSize="md"
              sx={{ marginBottom: "0 !important" }}
              data-testid={`profile-name-${profile.id}`}
            >
              {profile.fullName || t("asapProfile.profileCard.unknownName")}
            </Text>
            {profile.matchedConsultantId ? (
              <Tooltip
                label={t("asapProfile.profileCard.inDbTooltip")}
                hasArrow
              >
                <Badge
                  as={RouterLink}
                  to={`/portal/asap-pilot/consultants/${profile.matchedConsultantId}`}
                  colorScheme="green"
                  variant="solid"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  sx={{ marginBottom: "0 !important" }}
                  data-testid={`profile-in-db-${profile.id}`}
                >
                  {t("asapProfile.profileCard.inDbBadge")}
                </Badge>
              </Tooltip>
            ) : null}
          </HStack>
          <Text fontSize="sm" color="gray.600" sx={{ marginBottom: "2px !important" }}>
            {profile.currentTitle || "—"}
            {profile.currentCompany ? ` · ${profile.currentCompany}` : ""}
          </Text>
          {locationLabel && (
            <Text fontSize="xs" color="gray.500" sx={{ marginBottom: "0 !important" }}>
              {locationLabel}
            </Text>
          )}
          {profile.estimatedSeniority && (
            <Text fontSize="xs" color="gray.500" sx={{ marginBottom: "0 !important" }}>
              {t(`asapProfile.seniority.${profile.estimatedSeniority}`, {
                defaultValue: profile.estimatedSeniority,
              })}
            </Text>
          )}
        </Box>
        <VStack align="end" spacing={1}>
          <Badge
            colorScheme={scoreColorScheme(profile.score)}
            variant="solid"
            fontSize="md"
            px={3}
            py={1}
            borderRadius="md"
            data-testid={`profile-score-${profile.id}`}
          >
            {profile.score !== null && profile.score !== undefined
              ? profile.score
              : "—"}
          </Badge>
          {profile.linkedinUrl && (
            <Link
              href={profile.linkedinUrl}
              isExternal
              fontSize="xs"
              color="blue.600"
            >
              <HStack spacing={1}>
                <Text sx={{ marginBottom: "0 !important" }}>
                  {t("asapProfile.profileCard.linkedin")}
                </Text>
                <FaExternalLinkAlt size="0.7em" />
              </HStack>
            </Link>
          )}
        </VStack>
      </HStack>

      {tierChips.length > 0 && (
        <Wrap mt={2} spacing={1}>
          {tierChips.map((chip) => (
            <WrapItem key={`${chip.tier}:${chip.label}`}>
              <Tag
                size="sm"
                colorScheme={chip.color}
                variant="subtle"
                borderRadius="full"
                data-tier={chip.tier}
                data-testid={`profile-skill-${chip.tier}`}
              >
                <TagLabel>{chip.label}</TagLabel>
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}

      {!profile.matchedConsultantId && (
        <HStack mt={3} justify="flex-end">
          <Button
            size="sm"
            leftIcon={<FaUserPlus />}
            colorScheme="teal"
            variant="outline"
            isLoading={isAdding}
            onClick={handleAdd}
            data-testid={`profile-add-to-pilot-${profile.id}`}
          >
            {t("asapProfile.profileCard.addToPilot")}
          </Button>
        </HStack>
      )}
    </Box>
  );
};

export default ProfileCard;
